import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';

type JourneyPhase = 'departure' | 'rain' | 'tunnel' | 'dawn';

interface JourneyOptions {
  reducedMotion: boolean;
}

interface VideoScrubber {
  load(): void;
  setProgress(progress: number): void;
}

const PHASES: Array<{ phase: JourneyPhase; start: number }> = [
  { phase: 'departure', start: 0 },
  { phase: 'rain', start: 0.26 },
  { phase: 'tunnel', start: 0.51 },
  { phase: 'dawn', start: 0.77 },
];

function clamp(value: number): number {
  return Math.min(1, Math.max(0, value));
}

function localProgress(progress: number, start: number, end: number): number {
  return clamp((progress - start) / Math.max(0.001, end - start));
}

function currentPhase(progress: number): JourneyPhase {
  let phase: JourneyPhase = 'departure';
  for (const candidate of PHASES) {
    if (progress >= candidate.start) phase = candidate.phase;
  }
  return phase;
}

function activeVideoIndex(progress: number): number {
  if (progress < 0.26) return 0;
  if (progress < 0.51) return 1;
  if (progress < 0.77) return 2;
  return 3;
}

function createVideoScrubber(video: HTMLVideoElement, trimEndSeconds = 0): VideoScrubber {
  let duration = 0;
  let progress = 0;
  let desiredTime = 0;
  let loaded = false;
  let animationFrame = 0;

  video.muted = true;
  video.playsInline = true;
  video.pause();

  const syncMetadata = (): void => {
    duration = Number.isFinite(video.duration)
      ? Math.max(0, video.duration - trimEndSeconds - 0.001)
      : 0;
    desiredTime = duration * progress;
    video.pause();
    if (duration > 0 && video.currentTime === 0) video.currentTime = 0.001;
  };

  video.addEventListener('loadedmetadata', syncMetadata);
  if (video.readyState >= HTMLMediaElement.HAVE_METADATA) syncMetadata();

  const render = (): void => {
    if (duration > 0 && !video.seeking && Math.abs(video.currentTime - desiredTime) > 1 / 48) {
      video.currentTime = desiredTime;
    }
    animationFrame = requestAnimationFrame(render);
  };
  animationFrame = requestAnimationFrame(render);

  document.addEventListener('visibilitychange', () => {
    if (document.hidden) video.pause();
  });

  window.addEventListener('pagehide', () => cancelAnimationFrame(animationFrame), { once: true });

  return {
    load(): void {
      if (loaded) return;
      loaded = true;
      video.preload = 'auto';
      video.load();
    },
    setProgress(nextProgress: number): void {
      progress = clamp(nextProgress);
      if (duration > 0) desiredTime = duration * progress;
    },
  };
}

function setProgressRail(section: HTMLElement, phase: JourneyPhase): void {
  section.querySelectorAll<HTMLElement>('[data-journey-step]').forEach((item) => {
    item.classList.toggle('is-active', item.dataset.journeyStep === phase);
  });
}

function setJourneyData(
  progress: number,
  distance: HTMLElement,
  eta: HTMLElement,
): void {
  const values = progress < 0.18
    ? ['842 KM', '06:42 CET']
    : progress < 0.39
      ? ['716 KM', '06:28 CET']
      : progress < 0.61
        ? ['493 KM', '06:04 CET']
        : progress < 0.83
          ? ['286 KM', '05:41 CET']
          : ['118 KM', '05:22 CET'];

  distance.textContent = values[0];
  eta.textContent = values[1];
}

function preloadNearSection(section: HTMLElement, scrubbers: VideoScrubber[]): void {
  const observer = new IntersectionObserver((entries) => {
    if (!entries.some((entry) => entry.isIntersecting)) return;
    scrubbers[0]?.load();
    scrubbers[1]?.load();
    observer.disconnect();
  }, { rootMargin: '180% 0px 180% 0px' });
  observer.observe(section);
}

export function initOriginDestination({ reducedMotion }: JourneyOptions): void {
  const section = document.querySelector<HTMLElement>('#origin-destination');
  const stage = document.querySelector<HTMLElement>('#journey-stage');
  const videos = Array.from(document.querySelectorAll<HTMLVideoElement>('.journey-video'));
  const intro = document.querySelector<HTMLElement>('#journey-intro');
  const eyebrow = document.querySelector<HTMLElement>('.journey-eyebrow');
  const titleLines = Array.from(document.querySelectorAll<HTMLElement>('.journey-title-mask > span'));
  const introCopy = document.querySelector<HTMLElement>('.journey-intro-copy');
  const routeData = document.querySelector<HTMLElement>('#journey-route-data');
  const distance = document.querySelector<HTMLElement>('#journey-distance');
  const eta = document.querySelector<HTMLElement>('#journey-eta');
  const rainCopy = document.querySelector<HTMLElement>('#journey-rain-copy');
  const tunnelCopy = document.querySelector<HTMLElement>('#journey-tunnel-copy');
  const dawnStatement = document.querySelector<HTMLElement>('#journey-dawn-statement');
  const statementLines = Array.from(document.querySelectorAll<HTMLElement>('.journey-statement-mask > span'));
  const condition = document.querySelector<HTMLElement>('#journey-condition');
  const progressRail = document.querySelector<HTMLElement>('#journey-progress');
  const portal = document.querySelector<HTMLElement>('#journey-entry-portal');
  const gantryWipe = document.querySelector<HTMLElement>('#journey-gantry-wipe');
  const tunnelFrame = document.querySelector<HTMLElement>('#journey-tunnel-frame');
  const laneLine = document.querySelector<SVGSVGElement>('#journey-lane-line');
  const vignette = document.querySelector<HTMLElement>('#journey-vignette');
  const mist = document.querySelector<HTMLElement>('#journey-mist');

  if (
    !section || !stage || videos.length !== 4 || !intro || !eyebrow || titleLines.length !== 2 ||
    !introCopy || !routeData || !distance || !eta || !rainCopy || !tunnelCopy || !dawnStatement ||
    statementLines.length !== 2 || !condition || !progressRail || !portal || !gantryWipe ||
    !tunnelFrame || !laneLine || !vignette || !mist
  ) return;

  // Clips 02 and 03 contain imperfect closing frames. Their scroll windows
  // deliberately end two seconds before the physical media files do.
  const scrubbers = videos.map((video, index) =>
    createVideoScrubber(video, index === 1 || index === 2 ? 2 : 0));
  preloadNearSection(section, scrubbers);

  if (reducedMotion) {
    stage.style.visibility = 'visible';
    scrubbers[3]?.load();
    scrubbers[3]?.setProgress(0.56);
    section.dataset.journeyPhase = 'dawn';
    section.dataset.activeVideo = '3';
    return;
  }

  const isMobile = window.matchMedia('(max-width: 760px)').matches;
  let loadTier = 1;

  gsap.set([intro, routeData, rainCopy, tunnelCopy, dawnStatement, condition, progressRail], { opacity: 0 });
  gsap.set(eyebrow, { opacity: 0, y: 9, letterSpacing: '0.54em' });
  gsap.set(titleLines, { yPercent: 112, y: 0 });
  gsap.set(introCopy, { opacity: 0, y: 11, filter: 'blur(5px)' });
  gsap.set([rainCopy, tunnelCopy], { y: 10 });
  gsap.set(statementLines, { yPercent: 112, y: 0 });
  gsap.set(condition, { y: 8 });
  gsap.set(progressRail, { y: 8 });
  gsap.set(gantryWipe, { opacity: 0, xPercent: -145 });
  gsap.set(tunnelFrame, { opacity: 0, scale: 1.18 });
  gsap.set(laneLine, { opacity: 0, scaleY: 0.35, transformOrigin: '50% 100%' });
  gsap.set(videos, { opacity: 0, scale: 1.012, filter: 'brightness(.82) saturate(.88)' });
  gsap.set(videos[0], { opacity: 1 });

  const story = { progress: 0 };
  const syncStory = (): void => {
    const progress = clamp(story.progress);
    const phase = currentPhase(progress);
    const activeIndex = activeVideoIndex(progress);

    scrubbers[0]?.setProgress(localProgress(progress, 0, 0.26));
    scrubbers[1]?.setProgress(localProgress(progress, 0.26, 0.51));
    scrubbers[2]?.setProgress(localProgress(progress, 0.51, 0.77));
    scrubbers[3]?.setProgress(localProgress(progress, 0.77, 1));

    if (progress >= 0.22 && loadTier < 2) {
      loadTier = 2;
      scrubbers[2]?.load();
    }
    if (progress >= 0.55 && loadTier < 3) {
      loadTier = 3;
      scrubbers[3]?.load();
    }

    setJourneyData(progress, distance, eta);
    section.dataset.storyProgress = progress.toFixed(4);
    section.dataset.activeVideo = String(activeIndex);
    if (section.dataset.journeyPhase !== phase) {
      section.dataset.journeyPhase = phase;
      setProgressRail(section, phase);
    }
  };

  const timeline = gsap.timeline({
    defaults: { ease: 'power2.inOut' },
    scrollTrigger: {
      trigger: section,
      start: 'top top',
      end: () => `+=${Math.round(window.innerHeight * (isMobile ? 3.2 : 3.8))}`,
      scrub: isMobile ? 0.28 : 0.48,
      pin: stage,
      pinSpacing: false,
      anticipatePin: 1,
      invalidateOnRefresh: true,
      onEnter: () => { stage.style.visibility = 'visible'; },
      onEnterBack: () => { stage.style.visibility = 'visible'; },
      onLeave: () => { story.progress = 1; syncStory(); },
      onLeaveBack: () => {
        stage.style.visibility = 'hidden';
        story.progress = 0;
        syncStory();
      },
      onRefresh: (self) => {
        section.dataset.scrollStart = self.start.toFixed(2);
        section.dataset.scrollEnd = self.end.toFixed(2);
        if (self.progress > 0) stage.style.visibility = 'visible';
      },
    },
  });

  timeline
    .to(story, { progress: 1, duration: 1, ease: 'none', onUpdate: syncStory }, 0)

    // Section 04's bright terminal exit resolves into the wet access road.
    .to(portal, { opacity: 0, scale: 1.22, duration: 0.11, ease: 'power2.out' }, 0)
    .to(videos[0], { scale: 1, filter: 'brightness(.92) saturate(.9)', duration: 0.15, ease: 'none' }, 0)
    .to(intro, { opacity: 1, duration: 0.03 }, 0.065)
    .to(eyebrow, { opacity: 1, y: 0, letterSpacing: '0.34em', duration: 0.055 }, 0.069)
    .to(titleLines, { yPercent: 0, y: 0, duration: 0.065, stagger: 0.013, ease: 'power3.out' }, 0.077)
    .to(introCopy, { opacity: 1, y: 0, filter: 'blur(0px)', duration: 0.045 }, 0.107)
    .to(routeData, { opacity: 1, duration: 0.04 }, 0.12)
    .to(progressRail, { opacity: 1, y: 0, duration: 0.04 }, 0.13)
    .to([intro, routeData], { opacity: 0, y: -9, duration: 0.03 }, 0.215)

    // A motorway gantry passes close to camera and conceals the rain-sequence edit.
    .to(gantryWipe, { opacity: 1, xPercent: 145, duration: 0.05, ease: 'power2.inOut' }, 0.235)
    .set(videos[0], { opacity: 0 }, 0.26)
    .set(videos[1], { opacity: 1, scale: 1.01, filter: 'brightness(.8) saturate(.86)' }, 0.26)
    .to(gantryWipe, { opacity: 0, xPercent: 205, duration: 0.025 }, 0.27)
    .to(rainCopy, { opacity: 1, y: 0, duration: 0.04 }, 0.285)
    .to(condition, { opacity: 1, y: 0, duration: 0.035 }, 0.31)
    .to(mist, { opacity: 0.28, x: '1.2vw', duration: 0.17, ease: 'none' }, 0.3)
    .to([rainCopy, condition], { opacity: 0, y: -8, duration: 0.03 }, 0.465)

    // Road darkness and tunnel architecture hide the switch into the Alpine corridor.
    .to(gantryWipe, { opacity: 1, xPercent: -145, duration: 0 }, 0.48)
    .to(gantryWipe, { xPercent: 145, duration: 0.05 }, 0.48)
    .set(videos[1], { opacity: 0 }, 0.51)
    .set(videos[2], { opacity: 1, scale: 1.012, filter: 'brightness(.78) saturate(.82)' }, 0.51)
    .to(gantryWipe, { opacity: 0, xPercent: 205, duration: 0.025 }, 0.52)
    .to(tunnelCopy, { opacity: 1, y: 0, duration: 0.04 }, 0.535)
    .to(tunnelFrame, { opacity: 0.42, scale: 1, duration: 0.13, ease: 'none' }, 0.6)
    .to(vignette, { opacity: 0.72, duration: 0.11, ease: 'none' }, 0.62)
    .to(tunnelCopy, { opacity: 0, y: -8, duration: 0.03 }, 0.71)

    // Tunnel exit light becomes the pale Alpine dawn of the fourth clip.
    .to(portal, { opacity: 0.94, scale: 0.94, duration: 0.045, ease: 'power2.in' }, 0.735)
    .set(videos[2], { opacity: 0 }, 0.77)
    .set(videos[3], { opacity: 1, scale: 1.018, filter: 'brightness(.83) saturate(.82)' }, 0.77)
    .to([portal, tunnelFrame], { opacity: 0, duration: 0.04, ease: 'power2.out' }, 0.775)
    .to(videos[3], { scale: 1, filter: 'brightness(.96) saturate(.9)', duration: 0.16, ease: 'none' }, 0.77)
    .to(vignette, { opacity: 0.34, duration: 0.1 }, 0.78)
    .to(mist, { opacity: 0.17, x: '-1vw', duration: 0.16, ease: 'none' }, 0.78)
    .to(dawnStatement, { opacity: 1, duration: 0.035 }, 0.805)
    .to(statementLines, { yPercent: 0, y: 0, duration: 0.06, stagger: 0.013, ease: 'power3.out' }, 0.81)
    .to(condition, { opacity: 1, y: 0, duration: 0.035 }, 0.84)
    .to([dawnStatement, condition, progressRail], { opacity: 0, y: -8, duration: 0.035 }, 0.925)

    // Isolate one physical lane marking as the next-section transition handle.
    .to(laneLine, { opacity: 1, scaleY: 1, duration: 0.055, ease: 'power2.out' }, 0.93)
    .to(videos[3], { scale: 1.035, filter: 'brightness(.66) saturate(.72)', duration: 0.07, ease: 'none' }, 0.93)
    .to(vignette, { opacity: 0.75, duration: 0.06 }, 0.94)
    .to(laneLine, { scaleX: 0.68, duration: 0.04, ease: 'power2.inOut' }, 0.96);

  requestAnimationFrame(() => ScrollTrigger.refresh());
}
